// Wraps a large multipart upload with two things a plain axios.post doesn't
// do on its own: stall detection (this app sets no axios timeout, so a
// connection that goes quiet mid-transfer just hangs forever - there's no
// way for the examiner to tell "slow but working" from "dead"), and
// automatic retry-with-backoff for failures that never reached the server
// at all (a network drop, or our own stall-abort) - a real server-side
// rejection (validation error, 4xx) is never retried, since trying again
// won't change that outcome.
//
// This does not make the upload resumable - a retried attempt still
// re-sends the whole FormData from scratch (safe to do: File/Blob entries
// are immutable snapshots, not single-use streams). True chunked/resumable
// upload is tracked separately for the CAD bulk/comprehensive-submission
// path; this only closes the "silently hangs forever" and "one blip fails
// the whole upload" gaps for the common case.
import axios from 'axios';

const DEFAULT_STALL_TIMEOUT_MS = 45000; // no progress tick in 45s = treat as stalled
const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BACKOFF_BASE_MS = 2000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isStallOrNetworkFailure(err) {
  return err.isStall === true || !err.response;
}

// Single attempt, with stall detection layered on top via AbortController -
// axios has no native "abort if no progress for N seconds" option.
function uploadAttempt(url, formData, { onProgress, stallTimeoutMs, axiosConfig } = {}) {
  const controller = new AbortController();
  let stallTimer;

  const resetStallTimer = () => {
    clearTimeout(stallTimer);
    stallTimer = setTimeout(() => controller.abort(), stallTimeoutMs);
  };
  resetStallTimer();

  return axios
    .post(url, formData, {
      ...axiosConfig,
      signal: controller.signal,
      onUploadProgress: (event) => {
        resetStallTimer();
        onProgress?.(event);
      },
    })
    .finally(() => clearTimeout(stallTimer))
    .catch((err) => {
      if (axios.isCancel(err) || err.code === 'ERR_CANCELED') {
        const stallError = new Error(
          `Upload stalled - no progress for ${Math.round(stallTimeoutMs / 1000)}s`
        );
        stallError.isStall = true;
        throw stallError;
      }
      throw err;
    });
}

/**
 * @param {string} url
 * @param {FormData} formData
 * @param {object} [options]
 * @param {(event: ProgressEvent) => void} [options.onProgress]
 * @param {(attempt: number, maxAttempts: number) => void} [options.onRetry] - called before each retry (not the first attempt)
 * @param {number} [options.stallTimeoutMs]
 * @param {number} [options.maxAttempts]
 * @param {object} [options.axiosConfig] - extra axios config (e.g. headers)
 */
export async function resilientUpload(url, formData, options = {}) {
  const {
    onProgress,
    onRetry,
    stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    backoffBaseMs = DEFAULT_BACKOFF_BASE_MS,
    axiosConfig,
  } = options;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await uploadAttempt(url, formData, { onProgress, stallTimeoutMs, axiosConfig });
    } catch (err) {
      const isLastAttempt = attempt === maxAttempts;
      if (!isStallOrNetworkFailure(err) || isLastAttempt) {
        throw err;
      }
      onRetry?.(attempt, maxAttempts);
      await sleep(backoffBaseMs * 2 ** (attempt - 1));
    }
  }
  // Unreachable (the loop always returns or throws), kept for lint clarity.
  throw new Error('resilientUpload: exhausted attempts without a result');
}
